import {
  createSlice,
  createAsyncThunk,
  createSelector,
} from '@reduxjs/toolkit';
import api from '../../../app/api/api';
import { RootState } from '../../../app/store/configureStore';

interface Location {
  latitude?: number;
  longitude?: number;
  address: string;
}

interface Restaurant {
  id: string;
  name: string;
  logoUrl: string;
  location?: Location;
  distance?: number; // calculated field
}

export interface CustomerHomeState {
  restaurants: Restaurant[];
  loading: boolean;
  error: string | null;
  searchTerm: string;
  userLocation: Location | null;
}

const initialState: CustomerHomeState = {
  restaurants: [],
  loading: false,
  error: null,
  searchTerm: '',
  userLocation: null,
};

// Calculate distance between two points using Haversine formula
// Returns distance in kilometers, or Infinity if either location is missing coordinates
const calculateDistance = (loc1: Location, loc2: Location): number => {
  // Return Infinity if either location is missing coordinates
  if (
    loc1.latitude === undefined ||
    loc1.longitude === undefined ||
    loc2.latitude === undefined ||
    loc2.longitude === undefined
  ) {
    return Infinity;
  }

  const R = 6371; // Earth's radius in km
  const dLat = ((loc2.latitude - loc1.latitude) * Math.PI) / 180;
  const dLon = ((loc2.longitude - loc1.longitude) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((loc1.latitude * Math.PI) / 180) *
      Math.cos((loc2.latitude * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

export const fetchRestaurants = createAsyncThunk(
  'customerHome/fetchRestaurants',
  async () => {
    return await api.CustomerRestaurant.list();
  }
);

export const customerHomeSlice = createSlice({
  name: 'customerHome',
  initialState,
  reducers: {
    setSearchTerm: (state, action) => {
      state.searchTerm = action.payload;
    },
    setUserLocation: (state, action) => {
      state.userLocation = action.payload;
      if (state.userLocation) {
        state.restaurants = state.restaurants.map((restaurant) => ({
          ...restaurant,
          distance: restaurant.location
            ? calculateDistance(state.userLocation!, restaurant.location)
            : undefined,
        }));
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRestaurants.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRestaurants.fulfilled, (state, action) => {
        state.loading = false;
        state.restaurants = action.payload;
        if (state.userLocation) {
          state.restaurants = state.restaurants.map((restaurant) => ({
            ...restaurant,
            distance: restaurant.location
              ? calculateDistance(state.userLocation!, restaurant.location)
              : undefined,
          }));
        }
      })
      .addCase(fetchRestaurants.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch restaurants';
      });
  },
});

// Basic selectors
const selectRestaurants = (state: RootState) => state.customerHome.restaurants;
const selectSearchTerm = (state: RootState) => state.customerHome.searchTerm;
const selectUserLocation = (state: RootState) =>
  state.customerHome.userLocation;

// Memoized selector for filtered and sorted restaurants
export const selectFilteredRestaurants = createSelector(
  [selectRestaurants, selectSearchTerm, selectUserLocation],
  (restaurants, searchTerm, userLocation) => {
    let filteredRestaurants = restaurants;

    // Apply search filter
    if (searchTerm) {
      filteredRestaurants = restaurants.filter((restaurant: Restaurant) =>
        restaurant.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Sort by distance if location is available
    if (userLocation) {
      return [...filteredRestaurants].sort((a, b) => {
        const distanceA = a.distance || Number.MAX_VALUE;
        const distanceB = b.distance || Number.MAX_VALUE;
        return distanceA - distanceB;
      });
    }

    return filteredRestaurants;
  }
);

export const { setSearchTerm, setUserLocation } = customerHomeSlice.actions;
